import { ArrowLeft, TrendingDown } from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import welcomeBg from "../../assets/homebg.png";

const HRVDeviationPage = () => {
  const navigate = useNavigate();

  // Weekly HRV data (negative values indicate deviation below baseline)
  const weeklyData = [
    { day: "Mon", value: -5 },
    { day: "Tue", value: -8 },
    { day: "Wed", value: -3 },
    { day: "Thu", value: -12 },
    { day: "Fri", value: -10 },
    { day: "Sat", value: -6 },
    { day: "Sun", value: -14 },
  ];

  const maxValue = Math.max(...weeklyData.map((d) => Math.abs(d.value)));

  return (
    <div
      className="scrollbar-hide relative mx-auto w-full max-w-[400px] bg-background"
      style={{
        minHeight: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Blurred Background Image */}
      <div
        className="absolute inset-0 scale-105 bg-cover bg-center bg-no-repeat blur-md"
        style={{
          backgroundImage: `url(${welcomeBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />

      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-background/80" />

      <div
        className="scrollbar-hide relative z-10 space-y-4 overflow-y-auto p-4 pb-8"
        style={{ height: "calc(100vh - 0px)" }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="rounded-xl bg-card/80 hover:bg-card"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-semibold text-foreground">
            HRV Deviation
          </h1>
        </div>

        {/* Current Deviation Card */}
        <Card className="rounded-xl bg-card p-4 shadow-sm">
          <div className="mb-4 flex items-start justify-between">
            <div>
              <p className="mb-1 text-xs text-gray-500 dark:text-gray-400">
                Current Deviation
              </p>
              <div className="flex items-baseline gap-1">
                <span className="text-3xl font-bold text-foreground">-12%</span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  vs baseline
                </span>
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <div className="rounded-full bg-red-100 p-2 dark:bg-red-900/30">
                <TrendingDown className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <span className="rounded-md bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-400">
                No Data
              </span>
            </div>
          </div>

          {/* Load Impact Bar */}
          <div className="space-y-1">
            <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
              Load Impact
            </p>
            <div className="relative h-2 overflow-hidden rounded-full bg-gradient-to-r from-blue-500 via-blue-300 to-green-500">
              <div
                className="absolute left-0 top-0 h-full w-1 bg-gray-800 dark:bg-white"
                style={{ left: "0%" }}
              />
            </div>
            <div className="flex justify-between text-[10px] text-gray-500 dark:text-gray-400">
              <span>Low</span>
              <span>Moderate</span>
              <span>High</span>
            </div>
          </div>
        </Card>

        {/* Weekly HRV Deviation Chart */}
        <Card className="rounded-xl bg-card p-4 shadow-sm">
          <h3 className="mb-4 text-sm font-semibold text-foreground">
            Weekly HRV Deviation
          </h3>

          {/* Chart */}
          <div className="relative h-48">
            {/* Y-axis labels */}
            <div className="absolute left-0 top-0 flex h-full flex-col justify-between text-[10px] text-gray-500 dark:text-gray-400">
              <span>0</span>
              <span>-5</span>
              <span>-10</span>
              <span>-15</span>
            </div>

            {/* Chart area */}
            <div className="ml-6 flex h-full items-end justify-around gap-1">
              {weeklyData.map((item, index) => {
                const heightPercent = (Math.abs(item.value) / maxValue) * 100;
                return (
                  <div
                    key={index}
                    className="flex flex-1 flex-col items-center gap-1"
                  >
                    <div className="relative w-full" style={{ height: "160px" }}>
                      <div
                        className="absolute bottom-0 w-full rounded-t bg-red-500"
                        style={{ height: `${heightPercent}%` }}
                      />
                    </div>
                    <span className="text-[10px] text-gray-500 dark:text-gray-400">
                      {item.day}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>

        {/* What This Means Section */}
        <Card className="rounded-xl bg-blue-50/80 p-4 shadow-sm dark:bg-blue-900/20">
          <div className="mb-2 flex items-center gap-2">
            <div className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-blue-600 dark:border-blue-400">
              <span className="text-xs font-bold text-blue-600 dark:text-blue-400">
                i
              </span>
            </div>
            <h3 className="text-sm font-semibold text-foreground">
              What This Means
            </h3>
          </div>

          <div className="space-y-3 text-xs text-gray-700 dark:text-gray-300">
            <p>
              <span className="font-semibold">HRV deviation</span> measures how
              your current heart rate variability compares to your personal
              baseline. A negative deviation indicates you may be under more
              stress than usual.
            </p>

            <p>
              <span className="font-semibold text-red-600 dark:text-red-400">
                A -12% deviation
              </span>{" "}
              suggests elevated physiological load. This can be caused by poor
              sleep, high training load, illness, or psychological stress.
            </p>

            <p>
              <span className="font-semibold">Impact on Metabolism:</span> Lower
              HRV is associated with reduced glucose tolerance, meaning your
              body may process carbohydrates less efficiently today.
            </p>
          </div>
        </Card>

        {/* How to Improve Section */}
        <Card className="rounded-xl bg-card p-4 shadow-sm">
          <h3 className="mb-3 text-sm font-semibold text-foreground">
            How to Improve
          </h3>
          <ul className="space-y-2 text-xs text-gray-700 dark:text-gray-300">
            <li className="flex gap-2">
              <span className="text-red-500">•</span>
              <span>Prioritize 7-9 hours of quality sleep tonight</span>
            </li>
            <li className="flex gap-2">
              <span className="text-red-500">•</span>
              <span>
                Practice deep breathing or meditation for 10 minutes
              </span>
            </li>
            <li className="flex gap-2">
              <span className="text-red-500">•</span>
              <span>Avoid high-intensity exercise; opt for light walks</span>
            </li>
            <li className="flex gap-2">
              <span className="text-red-500">•</span>
              <span>Limit caffeine and alcohol intake</span>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default HRVDeviationPage;
